import React, { memo, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Collapse,
  Divider,
  Chip,
  Tooltip,
  Fade,
} from '@mui/material';
// Replace Grid with CSS Grid for simpler typing
import { styled } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  BookmarkBorder as BookmarkIcon,
} from '@mui/icons-material';

// Import our custom hooks
import { useFilterState } from '../../hooks/common';

// Import generic filter fields
import { FilterField, FilterFieldProps } from './FilterFields';

// Types
export interface FilterFieldConfig {
  key: string;
  label: string;
  fieldProps: FilterFieldProps;
  width?: number;
  required?: boolean;
}

export interface SavedFilter {
  id: string;
  name: string;
  filters: Record<string, any>;
  createdAt: Date;
}

export interface OptimizedFilterBarProps<T> {
  filters: T;
  onFiltersChange: (filters: T) => void;
  onSearch: () => void;
  onReset: () => void;
  fields: FilterFieldConfig[];
  loading?: boolean;
  hasSearched?: boolean;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  showSearchButton?: boolean;
  showResetButton?: boolean;
  showSaveButton?: boolean;
  savedFilters?: SavedFilter[];
  onSaveFilter?: (name: string, filters: T) => void;
  onLoadFilter?: (savedFilter: SavedFilter) => void;
  title?: string;
  showAdvancedToggle?: boolean;
  maxVisibleFields?: number;
}

// Styled Components
const FilterCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(3),
}));

const FilterHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const FilterContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const FilterActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

// Memoized Filter Field Component
const FilterFieldMemo = memo<{
  field: FilterFieldConfig;
  index: number;
  filters: Record<string, any>;
  onFiltersChange: (filters: Record<string, any>) => void;
}>(({ field, index, filters, onFiltersChange }) => {
  const handleFieldChange = useCallback((value: any) => {
    onFiltersChange({
      ...filters,
      [field.key]: value,
    });
  }, [field.key, filters, onFiltersChange]);

  return (
    <Box key={index}>
      <FilterField
        {...field.fieldProps}
        value={filters[field.key]}
        onChange={handleFieldChange}
      />
    </Box>
  );
});

// Main Optimized Filter Bar Component
const OptimizedFilterBar = <T extends Record<string, any>>({
  filters,
  onFiltersChange,
  onSearch,
  onReset,
  fields,
  loading = false,
  hasSearched = false,
  collapsible = true,
  defaultExpanded = true,
  showSearchButton = true,
  showResetButton = true,
  showSaveButton = false,
  savedFilters = [],
  onSaveFilter,
  onLoadFilter,
  title = 'Filters',
  showAdvancedToggle = false,
  maxVisibleFields = 3,
}: OptimizedFilterBarProps<T>) => {
  const {
    isFilterActive,
    resetFilters,
  } = useFilterState({
    initialFilters: filters,
    onFiltersChange,
  });

  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const [showSaveDialog, setShowSaveDialog] = React.useState(false);
  const [saveFilterName, setSaveFilterName] = React.useState('');

  // Memoized handlers
  const handleToggleExpanded = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  const handleToggleAdvanced = useCallback(() => {
    setShowAdvanced(prev => !prev);
  }, []);

  const handleSearch = useCallback(() => {
    onSearch();
  }, [onSearch]);

  const handleReset = useCallback(() => {
    resetFilters();
    onReset();
  }, [resetFilters, onReset]);

  const handleSaveFilter = useCallback(() => {
    if (saveFilterName.trim() && onSaveFilter) {
      onSaveFilter(saveFilterName.trim(), filters);
      setSaveFilterName('');
      setShowSaveDialog(false);
    }
  }, [saveFilterName, filters, onSaveFilter]);

  const handleLoadFilter = useCallback((savedFilter: SavedFilter) => {
    if (onLoadFilter) {
      onLoadFilter(savedFilter);
    }
  }, [onLoadFilter]);

  // Memoized filter fields
  const visibleFields = useMemo(() => {
    const fieldsToShow = showAdvanced ? fields : fields.slice(0, maxVisibleFields);
    return fieldsToShow.map((field, index) => (
      <FilterFieldMemo 
        key={field.key} 
        field={field} 
        index={index}
        filters={filters as Record<string, any>}
        onFiltersChange={onFiltersChange as unknown as (filters: Record<string, any>) => void}
      />
    ));
  }, [fields, showAdvanced, maxVisibleFields, filters, onFiltersChange]);

  // Memoized active filters count
  const activeFiltersCount = useMemo(() => {
    return Object.keys(filters).filter(key => {
      const value = filters[key];
      if (value === null || value === undefined || value === '') return false;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    }).length;
  }, [filters]);

  return (
    <FilterCard>
      <FilterHeader>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
          {activeFiltersCount > 0 && (
            <Chip
              label={`${activeFiltersCount} active`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {hasSearched && (
            <Chip
              label="Searched"
              size="small"
              color="success"
              variant="outlined"
            />
          )}
        </Box>
        
        <Box display="flex" alignItems="center" gap={1}>
          {showAdvancedToggle && fields.length > maxVisibleFields && (
            <Tooltip title={showAdvanced ? 'Hide advanced filters' : 'Show advanced filters'}>
              <IconButton onClick={handleToggleAdvanced} size="small">
                {showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          )}
          
          {collapsible && (
            <Tooltip title={expanded ? 'Collapse filters' : 'Expand filters'}>
              <IconButton onClick={handleToggleExpanded} size="small">
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </FilterHeader>

      <Collapse in={expanded}>
        <FilterContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {visibleFields}
          </Box>

          <FilterActions>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              {showSearchButton && (
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={loading}
                  size="small"
                >
                  {loading ? 'Searching...' : 'Search'}
                </Button>
              )}
              
              {showResetButton && (
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleReset}
                  disabled={loading}
                  size="small"
                >
                  Reset
                </Button>
              )}

              {showSaveButton && onSaveFilter && (
                <Tooltip title="Save current filters">
                  <IconButton
                    onClick={() => setShowSaveDialog(true)}
                    disabled={loading}
                    size="small"
                  >
                    <SaveIcon />
                  </IconButton>
                </Tooltip>
              )}

              {savedFilters.length > 0 && onLoadFilter && (
                <Tooltip title="Load saved filters">
                  <IconButton
                    onClick={() => {/* TODO: Implement saved filters dropdown */}}
                    disabled={loading}
                    size="small"
                  >
                    <BookmarkIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </FilterActions>
        </FilterContent>
      </Collapse>
    </FilterCard>
  );
};

export default memo(OptimizedFilterBar) as <T>(props: OptimizedFilterBarProps<T>) => JSX.Element;
